import { format, subDays } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import ResponsiveTable from '../components/ResponsiveTable';
import { EmptyState, FilterPanel } from '../components/ui';
import api from '../services/api';
import { ExcelColumn, exportToExcel } from '../utils/exportToExcel';

export interface InventoryTransactionRow {
  id: number;
  transactionType?: string;
  transaction_type?: string;
  quantity: number;
  previousQuantity?: number;
  previous_quantity?: number;
  newQuantity?: number;
  new_quantity?: number;
  reason?: string;
  referenceNumber?: string;
  reference_number?: string;
  transactionDate?: string;
  transaction_date?: string;
  notes?: string;
  performedBy?: number;
  performed_by?: number;
  inventoryId?: number;
  inventory_id?: number;
  inventory?: { wineName?: string; wine_name?: string };
  performer?: { firstName?: string; lastName?: string; first_name?: string; last_name?: string };
}

const TRANSACTION_TYPES = ['Add', 'Remove', 'Adjust', 'Sale', 'Damage', 'Transfer'];

const InventoryTransactions: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const [transactions, setTransactions] = useState<InventoryTransactionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<{ id: number; firstName?: string; lastName?: string; first_name?: string; last_name?: string }[]>([]);
  const [performedBy, setPerformedBy] = useState<string>('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (transactionTypeFilter) params.transactionType = transactionTypeFilter;
      if (performedBy) params.performedBy = performedBy;

      const [txRes, usersRes] = await Promise.all([
        api.get('/api/inventory/transactions', { params }),
        api.get('/api/users').catch(() => ({ data: [] }))
      ]);

      setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
      setUsers(Array.isArray(usersRes?.data) ? usersRes.data : []);
    } catch (err: any) {
      setError(err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, transactionTypeFilter, performedBy]);

  useEffect(() => {
    if (tabVisible) fetchData();
  }, [fetchData, tabVisible]);

  const filtered = searchQuery.trim()
    ? transactions.filter(
        (t) =>
          (t.reason ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (t.inventory?.wineName ?? (t.inventory as any)?.wine_name ?? '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : transactions;

  const getTypeColor = (type: string) => {
    const t = (type ?? '').toLowerCase();
    if (['add', 'adjust'].includes(t)) return 'bg-primary-100 text-primary-700';
    if (['remove', 'sale'].includes(t)) return 'bg-warning-100 text-warning-700';
    if (t === 'damage') return 'bg-error-100 text-error-700';
    if (t === 'transfer') return 'bg-secondary-100 text-secondary-700';
    return 'bg-monday-header text-monday-text-muted';
  };

  const columns = [
    {
      key: 'date',
      label: 'Date',
      render: (_: unknown, row: InventoryTransactionRow) => {
        const d = row.transactionDate ?? row.transaction_date;
        return d ? format(new Date(d), 'MMM d, yyyy HH:mm') : '–';
      }
    },
    {
      key: 'wine',
      label: 'Wine',
      render: (_: unknown, row: InventoryTransactionRow) =>
        (row.inventory as any)?.wineName ?? (row.inventory as any)?.wine_name ?? '–'
    },
    {
      key: 'type',
      label: 'Type',
      render: (_: unknown, row: InventoryTransactionRow) => {
        const t = row.transactionType ?? row.transaction_type ?? '–';
        return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(t)}`}>{t}</span>;
      }
    },
    {
      key: 'quantity',
      label: 'Change',
      render: (_: unknown, row: InventoryTransactionRow) => {
        const q = row.quantity ?? 0;
        const sign = q >= 0 ? '+' : '';
        return <span className={q >= 0 ? 'text-success-600' : 'text-error-600'}>{sign}{q}</span>;
      }
    },
    {
      key: 'previousNew',
      label: 'Previous → New',
      render: (_: unknown, row: InventoryTransactionRow) => {
        const prev = row.previousQuantity ?? row.previous_quantity ?? '–';
        const next = row.newQuantity ?? row.new_quantity ?? '–';
        return <span className="text-monday-text-muted">{prev} → {next}</span>;
      }
    },
    { key: 'reason', label: 'Reason', render: (_: unknown, row: InventoryTransactionRow) => row.reason ?? '–' },
    { key: 'reference', label: 'Reference', render: (_: unknown, row: InventoryTransactionRow) => row.referenceNumber ?? row.reference_number ?? '–' },
    {
      key: 'performer',
      label: 'Performed By',
      render: (_: unknown, row: InventoryTransactionRow) => {
        const p = row.performer;
        if (!p) return '–';
        return `${p.firstName ?? p.first_name ?? ''} ${p.lastName ?? p.last_name ?? ''}`.trim() || '–';
      }
    }
  ];

  const exportColumns: ExcelColumn<InventoryTransactionRow>[] = [
    { header: 'Date', accessor: (r) => (r.transactionDate ?? r.transaction_date) ? format(new Date(r.transactionDate ?? r.transaction_date!), 'yyyy-MM-dd HH:mm') : '' },
    { header: 'Wine', accessor: (r) => (r.inventory as any)?.wineName ?? (r.inventory as any)?.wine_name ?? '' },
    { header: 'Type', accessor: (r) => r.transactionType ?? r.transaction_type ?? '' },
    { header: 'Quantity Change', accessor: (r) => r.quantity },
    { header: 'Previous', accessor: (r) => r.previousQuantity ?? r.previous_quantity ?? '' },
    { header: 'New', accessor: (r) => r.newQuantity ?? r.new_quantity ?? '' },
    { header: 'Reason', accessor: (r) => r.reason ?? '' },
    { header: 'Reference', accessor: (r) => r.referenceNumber ?? r.reference_number ?? '' },
    {
      header: 'Performed By',
      accessor: (r) => {
        const p = r.performer;
        return p ? `${p.firstName ?? p.first_name ?? ''} ${p.lastName ?? p.last_name ?? ''}`.trim() : '';
      }
    }
  ];

  const handleExport = () => {
    exportToExcel({
      fileName: `inventory-transactions-${dateFrom}-to-${dateTo}.xlsx`,
      sheetName: 'Transactions',
      rows: filtered,
      columns: exportColumns
    });
  };

  if (loading && transactions.length === 0) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {error && <ErrorMessageComponent error={error} />}

      <FilterPanel title="Filters" collapsible defaultExpanded className="max-w-2xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-monday-text-muted mb-1">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full rounded border border-monday-border px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-monday-text-muted mb-1">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full rounded border border-monday-border px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-monday-text-muted mb-1">Transaction Type</label>
            <select
              value={transactionTypeFilter}
              onChange={(e) => setTransactionTypeFilter(e.target.value)}
              className="w-full rounded border border-monday-border px-2 py-1.5 text-sm"
            >
              <option value="">All</option>
              {TRANSACTION_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-monday-text-muted mb-1">Performed By</label>
            <select
              value={performedBy}
              onChange={(e) => setPerformedBy(e.target.value)}
              className="w-full rounded border border-monday-border px-2 py-1.5 text-sm"
            >
              <option value="">All</option>
              {users.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {u.firstName ?? u.first_name} {u.lastName ?? u.last_name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button type="button" onClick={() => { setDateFrom(format(subDays(new Date(), 30), 'yyyy-MM-dd')); setDateTo(format(new Date(), 'yyyy-MM-dd')); setTransactionTypeFilter(''); setPerformedBy(''); }} className="mt-2 text-sm text-primary-600 hover:underline">
          Clear filters
        </button>
      </FilterPanel>

      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="search"
          placeholder="Search by reason or wine..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="min-w-[200px] rounded-lg border border-monday-border px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={handleExport}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded border border-monday-border bg-white text-monday-text hover:bg-monday-header disabled:opacity-50"
        >
          Export CSV
        </button>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No transactions"
          message="No transaction history for the selected filters."
        />
      ) : (
        <ResponsiveTable
          columns={columns}
          data={filtered}
          emptyMessage="No transactions found."
          loading={loading}
        />
      )}
    </div>
  );
};

export default InventoryTransactions;
